const chapter = {
  id: 'plant-kingdom',
  slug: 'plant-kingdom',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Plant Kingdom',
        hindi: 'वनस्पति जगत',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Diversity in Living World', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'algae',
      title: {
        english: 'Algae',
        hindi: 'शैवाल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Algae = chlorophyll-bearing, simple, thalloid, autotrophic, largely aquatic organisms.',
        hindi: 'Algae = chlorophyll-bearing, सरल, थैलॉइड, स्वपोषी, बड़े पैमाने पर जलीय जीव।',
            },
            {
              english: 'Reproduction — vegetative (fragmentation), asexual (zoospores), sexual (isogamous, anisogamous, oogamous).',
        hindi: 'प्रजनन - वानस्पतिक (विखंडन), अलैंगिक (ज़ूस्पोर्स), लैंगिक (आइसोगैमस, अनिसोगैमस, ओगैमस)।',
            },
            {
              english: 'Three classes:',
        hindi: 'तीन वर्ग:',
            },
            {
              english: '• **Chlorophyceae (Green algae)** — chlorophyll a + b; reserve food = **starch**; cell wall = **cellulose**. Examples: *Chlamydomonas, Volvox, Spirogyra, Ulothrix, Chara*.',
        hindi: '• **क्लोरोफाइसी (हरा शैवाल)** — क्लोरोफिल ए + बी; आरक्षित food = **starch**; सेल wall = **cellulose**. उदाहरण: *क्लैमाइडोमोनस, वॉल्वॉक्स, स्पाइरोगाइरा, उलोथ्रिक्स, चरा*।',
            },
            {
              english: '• **Phaeophyceae (Brown algae)** — chlorophyll a + c, **fucoxanthin** (brown); reserve food = **mannitol + laminarin**; cell wall = cellulose + **algin**. Examples: *Ectocarpus, Dictyota, Laminaria, Sargassum, Fucus*.',
        hindi: '• **फियोफाइसी (भूरा शैवाल)** — क्लोरोफिल ए + सी, **फ्यूकोक्सैन्थिन** (भूरा); आरक्षित food = **mannitol + laminarin**; सेल wall = cellulose + **algin**. उदाहरण: *एक्टोकार्पस, डिक्टियोटा, लैमिनारिया, सारगासम, फ़्यूकस*।',
            },
            {
              english: '• **Rhodophyceae (Red algae)** — chlorophyll a + d, **r-phycoerythrin** (red); reserve = **floridean starch**; cell wall = cellulose + pectin + polysulphate esters. Mostly marine. Examples: *Polysiphonia, Porphyra, Gracilaria, Gelidium*.',
        hindi: '• **रोडोफाइसी (लाल शैवाल)** — क्लोरोफिल ए + डी, **आर-फाइकोएरिथ्रिन** (लाल); reserve = **floridean starch**; सेल wall = cellulose + pectin + polysulphate esters. अधिकतर समुद्री. उदाहरण: *पॉलीसिफ़ोनिया, पोर्फिरा, ग्रेसिलेरिया, गेलिडियम*।',
            },
            {
              english: 'Economic uses: > 70 species used as food (*Porphyra, Laminaria, Sargassum*); **agar** from *Gelidium* + *Gracilaria*; **carrageenan** from red algae; *Chlorella* + *Spirulina* used as protein-rich food (SCP).',
        hindi: 'आर्थिक उपयोग: > भोजन के रूप में उपयोग की जाने वाली 70 प्रजातियाँ (*पोर्फिरा, लैमिनारिया, सरगासुम*); **अगर** *गेलिडियम* + *ग्रेसिलेरिया* से; **कैरेजेनन** लाल शैवाल से; *क्लोरेला* + *स्पिरुलिना* का उपयोग प्रोटीन युक्त भोजन (एससीपी) के रूप में किया जाता है। **"पौधे साम्राज्य के उभयचर"** - मिट्टी में रहते हैं लेकिन यौन प्रजनन के लिए पानी पर निर्भर रहते हैं।',
            },
          ],
        },
      ],
    },
    {
      id: 'bryophytes',
      title: {
        english: 'Bryophytes',
        hindi: 'ब्रायोफाइटा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**"Amphibians of plant kingdom"** — live in soil but depend on water for sexual reproduction.',
        hindi: '**"पादप साम्राज्य के उभयचर"** - मिट्टी में रहते हैं लेकिन यौन प्रजनन के लिए पानी पर निर्भर रहते हैं।',
            },
            {
              english: 'Plant body = **gametophyte** (haploid, dominant); thalloid or differentiated into stem-like + leaf-like + rhizoids (no true roots/stems/leaves).',
        hindi: 'पौधा body = **gametophyte** (haploid, प्रमुख); थैलॉइड या तने जैसे + पत्ती जैसे + राइज़ोइड्स में विभेदित (कोई वास्तविक roots/stems/पत्तियाँ नहीं)।',
            },
            {
              english: 'Sex organs — male = **antheridium**, female = **archegonium**; biflagellate antherozoids → archegonium → fertilisation → **zygote → sporophyte**.',
        hindi: 'यौन अंग - male = **antheridium**, female = **archegonium**; बाइफ्लैगेलेट एथेरोज़ोइड्स → आर्कगोनियम → निषेचन → **जाइगोट → स्पोरोफाइट**।',
            },
            {
              english: 'Sporophyte = **partially / totally dependent on gametophyte**; produces haploid spores by meiosis.',
        hindi: 'Sporophyte = **partially / totally dependent on gametophyte**; अर्धसूत्रीविभाजन द्वारा अगुणित बीजाणु उत्पन्न करता है।',
            },
            {
              english: 'Two main groups:',
        hindi: 'दो मुख्य समूह:',
            },
            {
              english: '• **Liverworts** — *Marchantia* (thalloid, gemma cup for vegetative reproduction), *Riccia*.',
        hindi: '• **लिवरवॉर्ट्स** - *मार्चेंटिया* (थैलॉइड, वानस्पतिक प्रजनन के लिए जेम्मा कप), *रिकिया*।',
            },
            {
              english: '• **Mosses** — *Funaria, Polytrichum, Sphagnum*. Two stages: **protonema** (creeping, green, branched) + **leafy stage** (with whorl of leaves).',
        hindi: '• **मोसेस** - *फनेरिया, पॉलीट्रिचम, स्फाग्नम*। दो चरण: **प्रोटोनिमा** (रेंगता हुआ, हरा, शाखित) + **पत्तेदार चरण** (पत्तियों के झुंड के साथ)।',
            },
            {
              english: 'Economic importance — *Sphagnum* peat (fuel + packing); decomposers (rocks → soil); prevent soil erosion.',
        hindi: 'आर्थिक महत्व - *स्फाग्नम* पीट (ईंधन + पैकिंग); डीकंपोजर (चट्टानें → मिट्टी); मिट्टी के कटाव को रोकें.',
            },
          ],
        },
      ],
    },
    {
      id: 'pteridophytes',
      title: {
        english: 'Pteridophytes',
        hindi: 'टेरिडोफाइटा',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'First terrestrial plants to possess **vascular tissue (xylem + phloem)**.',
        hindi: '**संवहनी ऊतक (जाइलम + फ्लोएम)** रखने वाले पहले स्थलीय पौधे।',
            },
            {
              english: 'Sporophyte (dominant) — true roots, stem, leaves (microphylls in *Selaginella*; macrophylls in ferns).',
        hindi: 'स्पोरोफाइट (प्रमुख) - असली जड़ें, तना, पत्तियां (*सेलाजिनेला* में माइक्रोफिल; फर्न में मैक्रोफिल)।',
            },
            {
              english: 'Sporangia bear on **sporophylls**; in some, **strobili / cones** (e.g. *Selaginella, Equisetum*).',
        hindi: 'स्पोरैंगिया **स्पोरोफिल्स** पर होता है; कुछ में, **स्ट्रोबिली/शंकु** (जैसे *सेलाजिनेला, इक्विसेटम*)।',
            },
            {
              english: 'Spores → **prothallus** (gametophyte, photosynthetic, free-living).',
        hindi: 'बीजाणु → **प्रोथेलस** (गैमेटोफाइट, प्रकाश संश्लेषक, मुक्त-जीवित)।',
            },
            {
              english: 'Sex organs antheridia + archegonia on prothallus; **water needed for fertilisation**.',
        hindi: 'यौन अंग एथेरिडिया + प्रोथेलस पर आर्कगोनिया; **निषेचन के लिए पानी की आवश्यकता**।',
            },
            {
              english: 'Two types of spore production:',
        hindi: 'बीजाणु उत्पादन के दो प्रकार:',
            },
            {
              english: '• **Homosporous** — most pteridophytes (*Dryopteris*, ferns).',
        hindi: '• **होमोस्पोरस** - अधिकांश टेरिडोफाइट्स (*ड्रायोप्टेरिस*, फ़र्न)।',
            },
            {
              english: '• **Heterosporous** — *Selaginella, Salvinia* — produce 2 kinds of spores (megaspore + microspore); precursor to seed habit.',
        hindi: '• **हेटरोस्पोरस** - *सेलाजिनेला, साल्विनिया* - 2 प्रकार के बीजाणु पैदा करते हैं (मेगास्पोर + माइक्रोस्पोर); बीज की आदत का अग्रदूत.',
            },
            {
              english: 'Examples — *Selaginella, Equisetum* (horsetail), *Pteris, Adiantum* (ferns), *Marsilea*.',
        hindi: 'उदाहरण - *सेलाजिनेला, इक्विसेटम* (हॉर्सटेल), *पेरिस, एडियंटम* (फ़र्न), *मार्सिलिया*।',
            },
            {
              english: 'Classification — **Psilopsida (Psilotum), Lycopsida (Selaginella, Lycopodium), Sphenopsida (Equisetum), Pteropsida (Dryopteris, Pteris, Adiantum)**.',
        hindi: 'वर्गीकरण - **साइलोप्सिडा (साइलोटम), लाइकोप्सिडा (सेलाजिनेला, लाइकोपोडियम), स्फेनोप्सिडा (इक्विसेटम), टेरोप्सिडा (ड्रायोप्टेरिस, टेरिस, एडियंटम)**।',
            },
          ],
        },
      ],
    },
    {
      id: 'gymnosperms',
      title: {
        english: 'Gymnosperms',
        hindi: 'अनावृतबीजी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**"Naked seed" plants** — ovules + seeds **not enclosed by ovary wall**.',
        hindi: '**"नग्न बीज" पौधे** - बीजांड + बीज **अंडाशय की दीवार से घिरे नहीं**।',
            },
            {
              english: 'Range from medium to tall trees & shrubs; tallest gymnosperm = ***Sequoia*** (one of tallest tree species).',
        hindi: 'मध्यम से लेकर ऊंचे पेड़ों और झाड़ियों तक; सबसे ऊँचा gymnosperm = ***Sequoia*** (one of tallest tree species).',
            },
            {
              english: 'Roots — generally tap roots; in *Pinus*, **mycorrhiza** (fungal association); in *Cycas*, **coralloid roots** with N₂-fixing **cyanobacteria**.',
        hindi: 'जड़ें - आम तौर पर मूसला जड़ें; *पाइनस*, **माइकोराइजा** (फंगल एसोसिएशन) में; *साइकास* में, **कोरलॉइड जड़ें** एन₂-फिक्सिंग **सायनोबैक्टीरिया** के साथ।',
            },
            {
              english: 'Stem — unbranched (*Cycas*) or branched (*Pinus, Cedrus*).',
        hindi: 'तना - अशाखित (*साइकास*) या शाखित (*पाइनस, सेड्रस*)।',
            },
            {
              english: 'Leaves — simple/compound; needle-like in conifers (well-adapted to xeric: thick cuticle, sunken stomata).',
        hindi: 'पत्तियां - simple/यौगिक; कोनिफर्स में सुई की तरह (ज़ेरिक के लिए अच्छी तरह से अनुकूलित: मोटी छल्ली, धँसा रंध्र)।',
            },
            {
              english: 'Heterosporous — produce **microspores (pollen)** + **megaspores**; both develop into reduced gametophytes inside sporangia retained on sporophyll → highly reduced gametophytes.',
        hindi: 'हेटेरोस्पोरस - **माइक्रोस्पोर्स (पराग)** + **मेगास्पोर्स** का उत्पादन करता है; दोनों स्पोरोफिल पर बने स्पोरैंगिया के अंदर कम गैमेटोफाइट्स में विकसित होते हैं → अत्यधिक कम गैमेटोफाइट्स।',
            },
            {
              english: 'Pollination = **wind**; **no water required for fertilisation** (pollen tube delivers male gametes).',
        hindi: 'Pollination = **wind**; **निषेचन के लिए पानी की आवश्यकता नहीं** (पराग नली नर युग्मक प्रदान करती है)।',
            },
            {
              english: 'Seeds remain naked on megasporophylls — **no fruit formation**.',
        hindi: 'मेगास्पोरोफिल पर बीज नग्न रहते हैं - **कोई फल नहीं बनता**।',
            },
            {
              english: 'Examples — *Cycas, Pinus, Ginkgo, Cedrus, Ephedra*.',
        hindi: 'उदाहरण - *साइकस, पाइनस, जिन्कगो, सेड्रस, एफेड्रा*।',
            },
          ],
        },
      ],
    },
    {
      id: 'angiosperms',
      title: {
        english: 'Angiosperms (Flowering Plants)',
        hindi: 'आवृतबीजी (पुष्पीय पादप)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Pollen grains + ovules developed in specialised structures = **flowers**; seeds enclosed by **fruits**.',
        hindi: 'पराग कण + बीजांड विशेष structures = **flowers** में विकसित हुए; **फलों** से घिरे हुए बीज।',
            },
            {
              english: 'Range — *Wolffia* (smallest, ~ 0.1 cm) → **Eucalyptus** (> 100 m).',
        hindi: 'रेंज - *वोल्फिया* (सबसे छोटा, ~ 0.1 सेमी) → **यूकेलिप्टस** (> 100 मीटर)।',
            },
            {
              english: 'Two classes:',
        hindi: 'दो वर्ग:',
            },
            {
              english: '• **Dicotyledonae** — 2 cotyledons; reticulate venation; tap root; tetra/pentamerous flowers; vascular bundles open.',
        hindi: '• **डाइकोटाइलेडोने** — 2 बीजपत्र; Common Trapीदार शिराविन्यास; मूसला जड़; tetra/pentamerous फूल; संवहनी बंडल खुल जाते हैं।',
            },
            {
              english: '• **Monocotyledonae** — 1 cotyledon; parallel venation; fibrous root; trimerous flowers; vascular bundles closed.',
        hindi: '• **मोनोकोटाइलेडोने** — 1 बीजपत्र; समानांतर शिराविन्यास; रेशेदार जड़; ट्रिमर फूल; संवहनी बंडल बंद हो गए।',
            },
            {
              english: 'Male gametophyte = pollen grain; female gametophyte = embryo sac (**7 cells, 8 nuclei** typically).',
        hindi: 'नर गैमेटोफाइट = परागकण; मादा गैमेटोफाइट = भ्रूण थैली (**7 कोशिकाएं, 8 केन्द्रक** आम तौर पर)।',
            },
            {
              english: '**Double fertilisation** (unique to angiosperms):',
        hindi: '**दोहरा निषेचन** (एंजियोस्पर्म के लिए अद्वितीय):',
            },
            {
              english: '• 1 male gamete + egg → **zygote (2n)** → embryo.',
        hindi: '• 1 नर युग्मक + अंडा → **युग्मज (2n)** → भ्रूण।',
            },
            {
              english: '• 1 male gamete + 2 polar nuclei → **PEN (3n)** → endosperm.',
        hindi: '• 1 नर युग्मक + 2 ध्रुवीय केन्द्रक → **PEN (3n)** → भ्रूणपोष।',
            },
          ],
        },
      ],
    },
    {
      id: 'life-cycles',
      title: {
        english: 'Plant Life-Cycles & Alternation of Generations',
        hindi: 'पादप जीवन-चक्र और पीढ़ियों का एकान्तरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Haplontic** — only zygote diploid; meiosis at zygote; gametophyte dominant. Examples: most algae (*Spirogyra, Volvox, Chlamydomonas*).',
        hindi: '**हैप्लॉन्टिक** - केवल युग्मनज द्विगुणित; युग्मनज पर अर्धसूत्रीविभाजन; गैमेटोफाइट प्रमुख। उदाहरण: अधिकांश शैवाल (*स्पाइरोगाइरा, वॉल्वॉक्स, क्लैमाइडोमोनस*)।',
            },
            {
              english: '**Diplontic** — only gametes haploid; sporophyte dominant. Examples: seed plants (gymnosperms + angiosperms), *Fucus*.',
        hindi: '**डिप्लॉन्टिक** - केवल युग्मक अगुणित; स्पोरोफाइट प्रमुख. उदाहरण: बीज पौधे (जिम्नोस्पर्म + एंजियोस्पर्म), *फ्यूकस*।',
            },
            {
              english: '**Haplo-diplontic** — both phases multicellular; both photosynthetic.',
        hindi: '**हैप्लो-डिप्लॉन्टिक** - दोनों चरण बहुकोशिकीय; दोनों प्रकाश संश्लेषक.',
            },
            {
              english: '• Bryophytes — **gametophyte dominant**, sporophyte dependent.',
        hindi: '• ब्रायोफाइट्स - **गैमेटोफाइट प्रमुख**, स्पोरोफाइट पर निर्भर।',
            },
            {
              english: '• Pteridophytes — **sporophyte dominant**, free-living gametophyte.',
        hindi: '• टेरिडोफाइट्स - **स्पोरोफाइट प्रमुख**, मुक्त-जीवित गैमेटोफाइट।',
            },
            {
              english: 'Trend in evolution: **gametophyte reduction → sporophyte dominance**.',
        hindi: 'विकास में रुझान: **गैमेटोफाइट में कमी → स्पोरोफाइट प्रभुत्व**।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Algae chlorophyll: Green a+b, Brown a+c (fucoxanthin), Red a+d (r-phycoerythrin)}',
      desc: {
        english: '',
        hindi: 'शैवाल क्लोरोफिल: हरा ए+बी, भूरा ए+सी (फ्यूकोक्सैन्थिन), लाल ए+डी (आर-फाइकोएरिथ्रिन)',
      },
    },
    {
      latex: '\\text{Bryophytes: gametophyte dominant; "amphibians of plants"}',
      desc: {
        english: '',
        hindi: 'ब्रायोफाइट्स: गैमेटोफाइट प्रमुख; "पौधों के उभयचर"',
      },
    },
    {
      latex: '\\text{Pteridophytes: first vascular plants; prothallus = gametophyte}',
      desc: {
        english: '',
        hindi: 'टेरिडोफाइट्स: पहले संवहनी पौधे; प्रोथेलस = गैमेटोफाइट',
      },
    },
    {
      latex: '\\text{Gymnosperms: naked seeds, no fruit; pollen} \\rightarrow \\text{wind}',
      desc: {
        english: '',
        hindi: 'जिम्नोस्पर्म: नग्न बीज, कोई फल नहीं; पराग हवा',
      },
    },
    {
      latex: '\\text{Angiosperms: double fertilisation} \\rightarrow \\text{zygote (2n) + endosperm (3n)}',
      desc: {
        english: '',
        hindi: 'एंजियोस्पर्म: दोहरा निषेचन युग्मनज (2एन) + एंडोस्पर्म (3एन)',
      },
    },
    {
      latex: '\\text{Embryo sac: 7 cells, 8 nuclei}',
      desc: {
        english: '',
        hindi: 'भ्रूणकोष: 7 कोशिकाएँ, 8 केन्द्रक',
      },
    },
    {
      latex: '\\text{Haplontic (algae) | Haplo-diplontic (bryo, pterido) | Diplontic (seed plants)}',
      desc: {
        english: '',
        hindi: 'हैप्लॉन्टिक (शैवाल) | हाप्लो-डिप्लॉन्टिक (ब्रायो, पेरिडो) | डिप्लोन्टिक (बीज पौधे)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Algae',
      problem: {
        english: 'Reserve food in Phaeophyceae (brown algae) is:',
        hindi: 'फियोफाइसी (भूरा शैवाल) में आरक्षित भोजन है:',
      },
      answer: {
        english: 'Mannitol + Laminarin',
        hindi: 'मैनिटोल + लैमिनारिन',
      },
      solution: {
        english: 'Cell wall has algin.',
        hindi: 'कोशिका भित्ति में एल्गिन होता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Bryophyte',
      problem: {
        english: 'Plant group called "amphibians of plant kingdom":',
        hindi: 'पादप समूह को "पादप साम्राज्य के उभयचर" कहा जाता है:',
      },
      answer: {
        english: 'Bryophytes',
        hindi: 'ब्रायोफाइटा',
      },
      solution: {
        english: 'Need water for sexual reproduction.',
        hindi: 'यौन प्रजनन के लिए पानी की आवश्यकता होती है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Gymnosperm',
      problem: {
        english: 'Coralloid roots with cyanobacteria are found in:',
        hindi: 'सायनोबैक्टीरिया के साथ कोरलॉइड जड़ें पाई जाती हैं:',
      },
      answer: {
        english: 'Cycas',
        hindi: 'साइकस',
      },
      solution: {
        english: 'N₂-fixing symbiotic cyanobacteria.',
        hindi: 'एन₂-फिक्सिंग सहजीवी सायनोबैक्टीरिया।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Double fert',
      problem: {
        english: 'Endosperm of angiosperms is:',
        hindi: 'आवृतबीजी पौधों का भ्रूणपोष है:',
      },
      answer: {
        english: 'Triploid (3n)',
        hindi: 'त्रिगुणित (3n)',
      },
      solution: {
        english: '1 male gamete + 2 polar nuclei.',
        hindi: '1 नर युग्मक + 2 ध्रुवीय केन्द्रक।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'Pteridophyte',
      problem: {
        english: 'Heterosporous pteridophyte:',
        hindi: 'हेटरोस्पोरस टेरिडोफाइट:',
      },
      answer: {
        english: 'Selaginella / Salvinia',
        hindi: 'सेलाजिनेला / साल्विनिया',
      },
      solution: {
        english: 'Megaspores + microspores; precursor to seed habit.',
        hindi: 'मेगास्पोर्स + माइक्रोस्पोर्स; बीज की आदत का अग्रदूत.',
      },
    },
  ],
  next: {
    slug: 'animal-kingdom',
    title: 'Animal Kingdom',
  },
};

export default chapter;
